import { AlertTriangle, ChevronDown } from 'lucide-react';
import { useState } from 'react';

/**
 * Earnings & Risk Disclaimer.
 *
 * Always-visible 1-line summary + expandable full disclaimer. Designed to
 * appear at the bottom of EVERY page (marketing pages, auth pages, member
 * dashboard, admin console).
 *
 * IMPORTANT: This is a general-purpose compliance scaffold for an online
 * crypto-cycler platform. It is NOT a substitute for review by an attorney
 * licensed in the operator's jurisdiction. The platform owner should have
 * this language reviewed against local consumer-protection, securities,
 * AML/KYC, and FTC business-opportunity regulations before going live.
 */
const EarningsDisclaimer = ({ variant = 'default' }) => {
  const [open, setOpen] = useState(false);

  // Tighter visual treatment inside dashboard layouts vs the full marketing footer.
  const wrapClass = variant === 'compact' ? 'max-w-7xl mx-auto px-5 md:px-8 py-5' : 'max-w-7xl mx-auto px-5 md:px-8 py-6 md:py-7';

  return (
    <section
      className="relative border-t border-[rgba(255,182,18,0.10)] bg-[rgba(8,8,9,0.40)]"
      data-testid="earnings-disclaimer"
      aria-label="Earnings and Risk Disclaimer"
    >
      <div className={wrapClass}>
        <div className="flex items-start gap-3">
          <div
            className="shrink-0 w-8 h-8 rounded-lg flex items-center justify-center border"
            style={{
              background: 'rgba(255, 182, 18, 0.06)',
              borderColor: 'rgba(255, 182, 18, 0.32)',
            }}
            aria-hidden="true"
          >
            <AlertTriangle className="w-4 h-4 text-[#FFC20E]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#FFC20E] font-bold">Earnings &amp; Risk Disclaimer</div>
            <p className="text-[12px] md:text-[13px] leading-[1.65] text-[var(--sc-text-dim)] mt-1.5">
              Participation in 1SimpleCycler 3.0 involves real financial risk.{' '}
              <strong className="text-[#FFE08A]">No specific earnings, income, profits, or financial outcomes are promised or guaranteed.</strong>{' '}
              Cryptocurrency values are volatile and you may lose part or all of the funds you commit. This is not an investment offering and nothing on this
              site is financial, investment, tax, or legal advice.
            </p>

            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              className="mt-2 inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.22em] font-bold text-[#FFC20E] hover:text-[#FFE08A] transition"
              aria-expanded={open}
              aria-controls="earnings-disclaimer-full"
              data-testid="disclaimer-toggle"
            >
              {open ? 'Hide full disclaimer' : 'Read full disclaimer'}
              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>

            {open && (
              <div
                id="earnings-disclaimer-full"
                className="mt-4 space-y-3 text-[12px] md:text-[13px] leading-[1.7] text-[var(--sc-text-dim)] max-w-4xl"
                data-testid="disclaimer-full"
              >
                <p>
                  <strong className="text-[#FFE08A]">No Guaranteed Earnings.</strong> No income, return, profit, payout speed, cycle frequency, or financial
                  outcome of any amount is promised, projected, or guaranteed by 1SimpleCycler 3.0, its operators, its members, or its affiliates. Individual
                  results vary widely and depend on total platform activity, the size and behavior of the global participant pool, your personal level of
                  activity, prevailing market conditions, and external factors outside of the platform's control. Any illustrative numbers shown on this site or
                  in supporting materials are hypothetical examples only and should not be interpreted as a forecast.
                </p>

                <p>
                  <strong className="text-[#FFE08A]">Not a Security or Investment.</strong> Positions purchased on 1SimpleCycler 3.0 are not securities, shares,
                  units, investment contracts, interest-bearing accounts, deposits, derivatives, options, futures, or any other regulated financial instrument.
                  The platform does not pool funds for collective investment, does not promise interest, does not engage in trading on members' behalf, and does
                  not represent or imply any management of assets. Nothing on this site constitutes a solicitation to invest.
                </p>

                <p>
                  <strong className="text-[#FFE08A]">Not Financial, Investment, Tax, or Legal Advice.</strong> Nothing on this site, in marketing materials, in
                  member communications, or in support replies constitutes financial, investment, tax, accounting, or legal advice. Consult a qualified,
                  licensed professional in your jurisdiction before deciding whether to participate.
                </p>

                <p>
                  <strong className="text-[#FFE08A]">Cryptocurrency Risk.</strong> Litecoin (LTC), TRON (TRX), and Dogecoin (DOGE) are volatile, decentralized
                  digital assets. Their market value can rise or fall substantially in short time periods. Blockchain transactions are typically irreversible.
                  Network congestion may delay deposits or withdrawals. Sending funds to a wrong address, losing your private keys, or having your wallet
                  compromised can result in permanent loss. You are solely responsible for the security of your own wallets, seed phrases, and private keys.
                </p>

                <p>
                  <strong className="text-[#FFE08A]">Risk of Total Loss.</strong> You may lose some or all of the cryptocurrency you commit to the purchase of
                  positions on this platform. Only commit funds you can afford to lose entirely without impacting your basic living expenses or financial
                  obligations.
                </p>

                <p>
                  <strong className="text-[#FFE08A]">Member Activity Drives Outcomes.</strong> Cycle frequency, payout timing, and matching bonuses are directly
                  dependent on overall platform activity and the size of the global rotator pool. Periods of lower platform participation may result in slower
                  cycles or longer waiting times. There is no obligation on the platform or on any member to maintain a particular level of activity.
                </p>

                <p>
                  <strong className="text-[#FFE08A]">No Employment Relationship.</strong> Members of 1SimpleCycler 3.0 are independent participants and are not
                  employees, agents, contractors, partners, joint venturers, franchisees, or representatives of the platform or its operators. No member has
                  authority to make representations or commitments on behalf of the platform.
                </p>

                <p>
                  <strong className="text-[#FFE08A]">Tax Responsibility.</strong> You are solely responsible for determining what taxes apply to any
                  cryptocurrency you send to or receive from the platform and for reporting and paying those taxes in compliance with the laws of your
                  jurisdiction. The platform does not provide tax forms, tax advice, or tax withholding.
                </p>

                <p>
                  <strong className="text-[#FFE08A]">Eligibility &amp; Jurisdiction.</strong> You must be at least 18 years of age — or the age of majority in
                  your jurisdiction, whichever is greater — to create an account or participate. Participation may be restricted, regulated, or prohibited in
                  certain countries, states, provinces, or territories. It is your sole responsibility to ensure that your participation complies with all
                  applicable local, national, and international laws and regulations, including but not limited to consumer-protection, business-opportunity,
                  securities, and anti-money-laundering rules.
                </p>

                <p>
                  <strong className="text-[#FFE08A]">Finality of Cycles.</strong> Once a position has entered the live rotator pool and begun cycling, the
                  underlying purchase is final and non-refundable. Cycle payouts are non-reversible once dispatched on-chain.
                </p>

                <p>
                  <strong className="text-[#FFE08A]">Past Performance.</strong> Past cycle activity, historical pool size, member sign-up counts, or any
                  historical data shown on the platform does not predict or guarantee future cycle frequency, payout timing, or matching-bonus volume.
                </p>

                <p>
                  <strong className="text-[#FFE08A]">Forward-Looking Statements.</strong> Statements about how the platform will function, scale, or perform are
                  forward-looking and subject to change. Actual results may differ materially from any expectations set by marketing language.
                </p>

                <p className="text-[var(--sc-text-mute)] italic">
                  By creating an account and participating on 1SimpleCycler 3.0, you acknowledge that you have read, understood, and agreed to the full Terms of
                  Service, Privacy Policy, and this Earnings &amp; Risk Disclaimer.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default EarningsDisclaimer;
